/**
 * Pricing rules for store items.
 *
 * A store item has TWO possible sources of price, at different levels of the
 * model. Confusing them is the cause of the 2026-07-29 client report
 * ("prices not reflecting", "cannot edit prices of modified items"):
 *
 *   type.price               — on the item TYPE. A catalog default, shared by
 *                              every store that stocks that type.
 *   adjustedUnitRate.value   — on the STORE ITEM. The weighted average cost
 *                              derived from that store's stock-in transactions.
 *
 * Which one is displayed depends on the store:
 *
 *   store.enableDynamicPrice === true   → prefer adjustedUnitRate, fall back to
 *                                         type.price when there is no rate yet
 *   store.enableDynamicPrice !== true   → type.price is the price
 *
 * These are pure functions of a store-item document so they can be unit tested
 * and reused across surfaces (detail page, Excel export). Keeping them in one
 * place is deliberate: the detail page and the export previously implemented
 * the rule separately and drifted apart — the export emitted a literal 0.00
 * where the detail page correctly showed nothing.
 */

/** Value that reads as "no price", used by both rules below. */
const isUsablePrice = (n) => n != null && Number.isFinite(Number(n)) && Number(n) !== 0;

/**
 * The rate to display for an item, plus where it came from.
 *
 * A zero price counts as "no price". The detail page has always behaved this
 * way (its `if (unitRate)` guard is falsy at 0), and the export must match, or
 * unpriced items export as a 0.00 valuation rather than N/A.
 *
 * @param {object} item store item document (needs store, type, adjustedUnitRate)
 * @returns {{rate: number|null, source: 'catalog'|'weighted'|null}}
 */
export function resolveUnitRate(item) {
  if (!item || !item.type) return { rate: null, source: null };

  const dynamic = !!(item.store && item.store.enableDynamicPrice);
  const adjusted = item.adjustedUnitRate && item.adjustedUnitRate.value;
  const catalog = item.type.price;

  if (dynamic && isUsablePrice(adjusted)) {
    return { rate: Number(adjusted), source: 'weighted' };
  }
  if (isUsablePrice(catalog)) {
    return { rate: Number(catalog), source: 'catalog' };
  }
  return { rate: null, source: null };
}

/**
 * Total value of the holding: quantity x effective rate.
 * Null when either side is unknown, so callers can render N/A.
 */
export function resolveTotalValue(item) {
  const { rate } = resolveUnitRate(item);
  if (rate == null) return null;
  const qty = Number(item && item.quantity);
  if (!Number.isFinite(qty)) return null;
  return qty * rate;
}

/** Short label for the rate source, or null when there is no rate. */
export function rateSourceLabel(source) {
  if (source === 'weighted') return 'weighted avg';
  if (source === 'catalog') return 'catalog';
  return null;
}

/**
 * What the next stock-in will set the rate to.
 *
 * Mirrors rateManager.js on the backend. Recorded here because it is the reason
 * a manual Unit Rate override is NOT temporary: the current rate `r` becomes the
 * assumed cost of existing stock and is blended with the incoming delivery, so
 * an override permanently shifts the cost basis forward.
 *
 *   newRate = (q * r + tr * tq) / (q + tq)
 *
 * @param {number} q  quantity on hand
 * @param {number} r  item's current rate
 * @param {number} tq incoming quantity
 * @param {number} tr incoming rate
 */
export function weightedAverageAfterStockIn(q, r, tq, tr) {
  const total = q + tq;
  if (total === 0) return 0;
  const next = (q * r + tr * tq) / total;
  return Number.isFinite(next) ? next : r;
}
